/**
 * Create / edit modal for a SaaS catalog profile (VSNP-1150).
 *
 * SuperAdmin-only. A profile is the reusable definition (name + FQDN patterns)
 * that tenant admins pick from when creating a SaaS app. `slug` is the stable
 * identifier apps snapshot against and is immutable once created — a rename
 * would strand the tags that reference it.
 *
 * react-hook-form + Zod via the local resolver in utils/zodResolver.
 */

import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import Modal from '../common/Modal';
import AccessFormField from './AccessFormField';
import { zodResolver } from '../../utils/zodResolver';
import type { SaasProfile, SaasProfileData } from '../../services/access.service';

// One FQDN pattern: a bare host ("zoom.us") or a "*." suffix ("*.zoom.us").
const FQDN_PATTERN = /^(\*\.)?([a-z0-9-]+\.)+[a-z]{2,}$/i;

/** Split a comma/whitespace/newline separated list into trimmed, non-empty parts. */
function splitPatterns(raw?: string): string[] {
  return (raw ?? '')
    .split(/[\s,]+/)
    .map((p) => p.trim())
    .filter(Boolean);
}

const profileSchema = z.object({
  slug: z
    .string()
    .min(1, 'Slug is required')
    .max(64)
    .regex(/^[a-z0-9][a-z0-9._-]*$/, 'Lowercase letters, digits and . _ - only'),
  name: z.string().min(1, 'Name is required').max(128),
  description: z.string().max(512).optional(),
  category: z.string().max(64).optional(),
  fqdnPatternsCsv: z
    .string()
    .min(1, 'Add at least one FQDN pattern')
    .refine((raw) => splitPatterns(raw).length > 0, 'Add at least one FQDN pattern')
    .refine(
      (raw) => splitPatterns(raw).every((p) => FQDN_PATTERN.test(p)),
      'Each pattern must be a hostname or "*." suffix (e.g. zoom.us or *.zoom.us)',
    ),
});

type ProfileFormValues = z.infer<typeof profileSchema>;

interface SaasProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: SaasProfileData) => Promise<void>;
  /** When present, the modal edits this profile instead of creating one. */
  profile?: SaasProfile | null;
}

const SaasProfileModal = ({ isOpen, onClose, onSave, profile }: SaasProfileModalProps) => {
  const isEdit = !!profile;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ProfileFormValues>({ resolver: zodResolver<ProfileFormValues>(profileSchema) });

  // Reset to the edited profile's values (or blanks) each time the modal opens.
  useEffect(() => {
    if (!isOpen) return;
    reset({
      slug: profile?.slug ?? '',
      name: profile?.name ?? '',
      description: profile?.description ?? '',
      category: profile?.category ?? '',
      fqdnPatternsCsv: (profile?.fqdn_patterns ?? []).join('\n'),
    });
  }, [isOpen, profile, reset]);

  const submit = handleSubmit(async (values) => {
    // `enabled` is intentionally omitted: a new profile is created enabled by
    // default, and enable/disable is a table row action (the Actions toggle),
    // not a create/edit-modal field — so an edit never touches enabled.
    await onSave({
      slug: values.slug,
      name: values.name,
      description: values.description?.trim() ? values.description : null,
      category: values.category?.trim() ? values.category : null,
      fqdnPatterns: splitPatterns(values.fqdnPatternsCsv),
    });
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? `Edit ${profile?.name}` : 'Add SaaS Profile'}
      footer={
        <div className="flex justify-end space-x-2">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </button>
          <button type="button" className="btn-primary" onClick={submit} disabled={isSubmitting}>
            {isSubmitting ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Profile'}
          </button>
        </div>
      }
    >
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <AccessFormField
            label="Slug"
            htmlFor="saas-slug"
            required
            error={errors.slug?.message}
            hint={isEdit ? 'Immutable identifier apps link against.' : 'Stable identifier, e.g. "zoom".'}
          >
            <input
              id="saas-slug"
              className="input-base w-full"
              placeholder="zoom"
              readOnly={isEdit}
              {...register('slug')}
            />
          </AccessFormField>

          <AccessFormField label="Name" htmlFor="saas-name" required error={errors.name?.message}>
            <input id="saas-name" className="input-base w-full" placeholder="Zoom" {...register('name')} />
          </AccessFormField>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <AccessFormField
            label="Category"
            htmlFor="saas-category"
            error={errors.category?.message}
            hint="Optional grouping, e.g. Collaboration."
          >
            <input id="saas-category" className="input-base w-full" placeholder="Collaboration" {...register('category')} />
          </AccessFormField>

          <AccessFormField label="Description" htmlFor="saas-desc" error={errors.description?.message}>
            <input id="saas-desc" className="input-base w-full" {...register('description')} />
          </AccessFormField>
        </div>

        <AccessFormField
          label="FQDN Patterns"
          htmlFor="saas-fqdns"
          required
          error={errors.fqdnPatternsCsv?.message}
          hint='A leading "*." matches all subdomains. One per line, or comma separated.'
        >
          <textarea
            id="saas-fqdns"
            rows={5}
            className="input-base w-full font-mono text-sm"
            placeholder={'zoom.us\n*.zoom.us\n*.zoom.com'}
            {...register('fqdnPatternsCsv')}
          />
        </AccessFormField>
      </form>
    </Modal>
  );
};

export default SaasProfileModal;
